export function isObject(o) {
  return typeof o === 'object' && o !== null;
}
export function isFunction(f) {
  return Object.prototype.toString.call(f) === '[object Function]';
}
export function reduceValues(record, reducer, initialValue = {}) {
  return Object.values(record).reduce(reducer, initialValue);
}

/**
 * Performs a deep merge of objects and returns new object. Does not modify
 * objects (immutable) and merges arrays via concatenation.
 * https://stackoverflow.com/a/48218209/15101549
 */
export function merge(...objects) {
  return objects.reduce((prev, obj) => {
    Object.keys(obj).forEach(key => {
      const pVal = prev[key];
      const oVal = obj[key];
      const pType = Object.prototype.toString.call(pVal);
      const oType = Object.prototype.toString.call(oVal);
      if (pType === '[object Array]' && oType === '[object Array]') {
        prev[key] = pVal.concat(...oVal);
      } else if (pType === '[object Object]' && oType === '[object Object]') {
        prev[key] = merge(pVal, oVal);
      } else {
        prev[key] = oVal;
      }
    });
    return prev;
  }, Array.isArray(objects[0]) ? [] : {});
}

// IMPORTANT: This deepEquals function is only appropriate to use on JSON objects (without Date or RegExp values)
// Largely based on the dequal package by lukeed, minus Date and RegExp capabilities: https://www.npmjs.com/package/dequal
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function deepEquals(foo, bar) {
  let ctor, len;
  if (foo === bar) {
    return true;
  }
  if (foo && bar && (ctor = foo.constructor) === bar.constructor) {
    if (ctor === Array) {
      if ((len = foo.length) === bar.length) {
        // eslint-disable-next-line no-empty
        while (len-- && deepEquals(foo[len], bar[len])) {}
      }
      return len === -1;
    }
    if (!ctor || typeof foo === 'object') {
      len = 0;
      // eslint-disable-next-line guard-for-in
      for (ctor in foo) {
        if (Object.prototype.hasOwnProperty.call(foo, ctor) && ++len && !Object.prototype.hasOwnProperty.call(bar, ctor)) {
          return false;
        }
        if (!(ctor in bar) || !deepEquals(foo[ctor], bar[ctor])) {
          return false;
        }
      }
      return Object.keys(bar).length === len;
    }
  }

  // eslint-disable-next-line no-self-compare
  return foo !== foo && bar !== bar;
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze#what_is_shallow_freeze
export function deepFreeze(object) {
  const propNames = Object.getOwnPropertyNames(object);
  for (const name of propNames) {
    const value = object[name];
    if (value && typeof value === 'object') {
      deepFreeze(value);
    }
  }
  return Object.freeze(object);
}
export function deepCopy(obj, seen = new WeakMap()) {
  // Handle primitive values
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  // Handle circular references
  if (seen.has(obj)) {
    return seen.get(obj);
  }

  // Handle Date objects
  if (obj instanceof Date) {
    const copy = new Date(obj.getTime());
    seen.set(obj, copy);
    return copy;
  }

  // Handle RegExp objects
  if (obj instanceof RegExp) {
    const copy = new RegExp(obj.source, obj.flags);
    seen.set(obj, copy);
    return copy;
  }

  // Handle Map objects
  if (obj instanceof Map) {
    const copy = new Map();
    seen.set(obj, copy);
    obj.forEach((value, key) => {
      copy.set(deepCopy(key, seen), deepCopy(value, seen));
    });
    return copy;
  }

  // Handle Set objects
  if (obj instanceof Set) {
    const copy = new Set();
    seen.set(obj, copy);
    obj.forEach(value => {
      copy.add(deepCopy(value, seen));
    });
    return copy;
  }

  // Handle Array objects
  if (Array.isArray(obj)) {
    const copy = obj.map(item => deepCopy(item, seen));
    seen.set(obj, copy);
    return copy;
  }

  // Handle Typed Arrays
  if (ArrayBuffer.isView(obj)) {
    const copy = new obj.constructor(obj.buffer);
    seen.set(obj, copy);
    return copy;
  }

  // Handle Error objects
  if (obj instanceof Error) {
    const copy = new Error(obj.message);
    copy.stack = obj.stack;
    seen.set(obj, copy);
    return copy;
  }

  // Handle plain objects
  if (obj instanceof Object) {
    const copy = Object.create(Object.getPrototypeOf(obj));
    seen.set(obj, copy);

    // Copy all properties including Symbols
    const allKeys = [...Object.getOwnPropertyNames(obj), ...Object.getOwnPropertySymbols(obj)];
    for (const key of allKeys) {
      copy[key] = deepCopy(obj[key], seen);
    }
    return copy;
  }

  // Fallback for any other type
  return obj;
}